import React, { useState, useEffect } from 'react';
import { campaignsAPI } from '../api/campaigns';
import { gamesAPI } from '../api/games';
import { useAuth } from '../context/AuthContext';
import CampaignKpiEditor from './CampaignKpiEditor';
import { emptyKpis } from '../utils/campaignKpis';

const AddCampaignModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [gameSearch, setGameSearch] = useState('');
  const [showGameDropdown, setShowGameDropdown] = useState(false);
  // const [selectedGame, setSelectedGame] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'sponsored',
    gameId: '',
    clientName: '',
    startDate: '',
    endDate: '',
    budget: '',
    keysAvailable: '',
    status: 'active',
    goals: '',
    notes: '',
    titleKeywords: '',
    eligibleCategories: 'Special Events, Just Chatting',
    hashtags: '',
    kpis: emptyKpis()
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchGames();
    }
  }, [isOpen]);

  const fetchGames = async () => {
    try {
      const response = await gamesAPI.getAll({ limit: 500 });
      setGames(response.data.games);
      setFilteredGames(response.data.games);
    } catch (err) {
      console.error('Failed to fetch games:', err);
    }
  };

  const handleGameSearch = async (value) => {
    setGameSearch(value);
    setShowGameDropdown(true);

    if (value.trim().length >= 2) {
      // Use server-side search for better results
      try {
        const response = await gamesAPI.getAll({ search: value, limit: 50 });
        setFilteredGames(response.data.games);
      } catch (err) {
        console.error('Failed to search games:', err);
        // Fallback to client-side filter
        const filtered = games.filter(g =>
          g.title.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredGames(filtered);
      }
    } else if (value.trim()) {
      // For single character, use client-side filter
      const filtered = games.filter(g =>
        g.title.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredGames(filtered);
    } else {
      setFilteredGames(games);
    }
  };

  const handleGameInputFocus = async () => {
    if (games.length === 0) {
      // Games haven't loaded yet, fetch them
      try {
        const response = await gamesAPI.getAll({ limit: 500 });
        setGames(response.data.games);
        setFilteredGames(response.data.games);
      } catch (err) {
        console.error('Failed to fetch games:', err);
      }
    } else {
      setFilteredGames(games);
    }
    setShowGameDropdown(true);
  };

  const selectGame = (game) => {
    // setSelectedGame(game);
    setGameSearch(game.title);
    setFormData({ ...formData, gameId: game.id });
    setShowGameDropdown(false);
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Game must be an actual selection from the dropdown (sets gameId), not just typed text.
    if (formData.type !== 'showcase' && !formData.gameId) {
      setError('Please select a game from the dropdown.');
      setLoading(false);
      return;
    }

    try {
      const splitList = (s) => (s || '').split(',').map(v => v.trim()).filter(Boolean);
      const dataToSend = {
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        budget: (formData.type === 'sponsored' || formData.type === 'sponsored_organic') ? parseFloat(formData.budget) || null : null,
        keysAvailable: (formData.type === 'organic' || formData.type === 'sponsored_organic') ? parseInt(formData.keysAvailable) || null : null,
        gameId: formData.type === 'showcase' ? null : (formData.gameId || null),
        titleKeywords: formData.type === 'showcase' ? splitList(formData.titleKeywords) : [],
        eligibleCategories: formData.type === 'showcase' ? splitList(formData.eligibleCategories) : [],
        hashtags: (formData.hashtags || '').split(',').map(h => h.trim().replace(/^#/, '').toLowerCase()).filter(Boolean),
        kpis: formData.kpis,
        createdBy: user.id
      };

      await campaignsAPI.create(dataToSend);
      onSuccess();
      onClose();

      // Reset form
      setFormData({
        name: '',
        type: 'sponsored',
        gameId: '',
        clientName: '',
        startDate: '',
        endDate: '',
        budget: '',
        keysAvailable: '',
        status: 'active',
        goals: '',
        notes: '',
        titleKeywords: '',
        eligibleCategories: 'Special Events, Just Chatting',
        hashtags: '',
        kpis: emptyKpis()
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Create New Campaign</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Campaign Type Toggle */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Campaign Type *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.type === 'sponsored'
                  ? 'border-sc-pink bg-sc-pink/10'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="type"
                  value="sponsored"
                  checked={formData.type === 'sponsored'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="text-2xl mb-1">💰</div>
                  <div className="font-medium">Sponsored</div>
                  <div className="text-xs text-gray-500">Paid partnerships</div>
                </div>
              </label>

              <label className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.type === 'organic'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="type"
                  value="organic"
                  checked={formData.type === 'organic'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="text-2xl mb-1">🎁</div>
                  <div className="font-medium">Organic</div>
                  <div className="text-xs text-gray-500">Key sends / No budget</div>
                </div>
              </label>

              <label className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.type === 'sponsored_organic'
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="type"
                  value="sponsored_organic"
                  checked={formData.type === 'sponsored_organic'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="text-2xl mb-1">💰🎁</div>
                  <div className="font-medium">Sponsored + Organic</div>
                  <div className="text-xs text-gray-500">Budget & key sends</div>
                </div>
              </label>

              <label className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.type === 'showcase'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="type"
                  value="showcase"
                  checked={formData.type === 'showcase'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="text-2xl mb-1">🎤</div>
                  <div className="font-medium">Showcase</div>
                  <div className="text-xs text-gray-500">Event co-streams (no game)</div>
                </div>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Campaign Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Call of Duty MW3 Launch Campaign"
              />
            </div>

            {/* Game (hidden for showcase campaigns) */}
            {formData.type !== 'showcase' && (
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Game *
                </label>
                <input
                  type="text"
                  placeholder="Search games..."
                  value={gameSearch}
                  onChange={(e) => handleGameSearch(e.target.value)}
                  onFocus={handleGameInputFocus}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                />
                {showGameDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto z-10">
                    {filteredGames.length > 0 ? (
                      filteredGames.map((game) => (
                        <div
                          key={game.id}
                          onClick={() => selectGame(game)}
                          className="px-3 py-2 hover:bg-sc-pink/10 cursor-pointer text-sm"
                        >
                          {game.title}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">No games found</div>
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Don't see your game? Add it in the Games section first.
                </p>
              </div>
            )}

            {/* Showcase-only: title keywords + eligible Twitch categories */}
            {formData.type === 'showcase' && (
              <>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title Keywords *
                  </label>
                  <input
                    type="text"
                    name="titleKeywords"
                    value={formData.titleKeywords}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                    placeholder="bitsummit, punch mixtape, gamesradar"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Comma-separated. A stream attributes if its Twitch title contains any of these (case-insensitive).
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Eligible Twitch Categories *
                  </label>
                  <input
                    type="text"
                    name="eligibleCategories"
                    value={formData.eligibleCategories}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                    placeholder="Special Events, Just Chatting"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Stream's Twitch category must be one of these AND the title must match a keyword.
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hashtags *
                  </label>
                  <input
                    type="text"
                    name="hashtags"
                    value={formData.hashtags}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                    placeholder="bitsummit, punchmixtape, gamesradar"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Comma-separated, no #. Used to attribute Twitter, Instagram, YouTube, and TikTok posts to this campaign.
                  </p>
                </div>
              </>
            )}

            {/* Client Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Name *
              </label>
              <input
                type="text"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Activision"
              />
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
              />
            </div>

            {/* Budget (for Sponsored / Sponsored + Organic) */}
            {(formData.type === 'sponsored' || formData.type === 'sponsored_organic') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Budget (USD) *
                </label>
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                  placeholder="50000"
                />
              </div>
            )}

            {/* Keys Available (for Organic / Sponsored + Organic) */}
            {(formData.type === 'organic' || formData.type === 'sponsored_organic') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keys Available *
                </label>
                <input
                  type="number"
                  name="keysAvailable"
                  value={formData.keysAvailable}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                  placeholder="500"
                />
              </div>
            )}

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
              >
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Goals */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Goals *
              </label>
              <textarea
                name="goals"
                value={formData.goals}
                onChange={handleChange}
                rows="2"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="• Generate 10M+ total views&#10;• Target 5% engagement rate"
              />
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes *
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="2"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Any additional notes..."
              />
            </div>

            {/* Campaign Hashtags (Showcase has its own hashtags field above) */}
            {formData.type !== 'showcase' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign Hashtags *
                </label>
                <input
                  type="text"
                  name="hashtags"
                  value={formData.hashtags}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                  placeholder="helldivers2partner, ad, sponsored (comma-separated)"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Content with these hashtags will be auto-linked to this campaign during scans
                </p>
              </div>
            )}

            {/* Campaign KPIs (sections shown by type) */}
            <CampaignKpiEditor
              type={formData.type}
              value={formData.kpis}
              onChange={(kpis) => setFormData({ ...formData, kpis })}
            />
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-sc-pink text-white rounded-md hover:bg-sc-pink/90 disabled:bg-gray-400"
            >
              {loading ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCampaignModal;
